<template>
    <td
        role="cell"
        :style="containerStyle"
        :class="containerClass"
        @click="setFocus"
        :data-p-frozen-column="frozen"
        :data-p-editable-column="editableCell"
        ref="cellRef"
    >
        <div class="md-editable-grid-cell-wrapper" @dblclick="onCellDblClick">
            <!-- Editing mode -->
            <div
                v-if="isEditing"
                v-click-outside:[datePickerName]="closeDropdown"
                class="editable-date-cell-content-wrapper"
            >
                <div
                    :class="{
                        'md-editable-grid-cell-content-invalid': invalid,
                        'md-editable-grid-cell-editing': !invalid
                    }"
                    class="md-editable-grid-cell-content"
                    contenteditable="true"
                    ref="inputRef"
                    @keydown="handleKeydown"
                    @blur="finishEdit"
                >
                    {{ displayValue }}
                </div>

                <div class="editable-date-cell-icon-container" @mousedown.prevent="toggleDropdown">
                    <i>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1024 1024"
                            width="1em"
                            height="1em"
                            fill="#19BEE8"
                        >
                            <path
                                d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
                            ></path>
                        </svg>
                    </i>
                </div>
            </div>

            <!-- Display mode -->
            <div v-else class="editable-date-cell-display-wrapper">
                <span
                    class="md-editable-grid-cell-display"
                    :class="{
                        'md-editable-grid-cell-non-editable': !editableCell,
                        'span-placeholder': placeholderVisible
                    }"
                    @dblclick="requestEditHandler"
                    @keydown="handleDisplayKeydown"
                    tabindex="0"
                    ref="displayRef"
                >
                    {{ displayValue || placeholder }}
                </span>

                <!-- DateTime picker icon -->
                <div
                    v-if="editableCell"
                    class="editable-date-cell-display-icon"
                    @mousedown.prevent="requestEditHandler"
                >
                    <i>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 1024 1024"
                            width="1em"
                            height="1em"
                            fill="#19BEE8"
                        >
                            <path
                                d="M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
                            ></path>
                        </svg>
                    </i>
                </div>
            </div>

            <date-picker
                v-show="isEditing && showDropdown"
                v-model="timeValue"
                :type="'time'"
                :inline="true"
                :format="dateFormat"
                :lang="lang"
                :id="datePickerName"
                input-class="form-control"
                @change="datePickerChanged"
            >
                <template v-slot:header="{ emit }">
                    <button class="mx-btn mx-btn-text mx-btn-local" @click="selectToday(emit)">
                        {{ $t('Shared.Now') }}
                    </button>
                </template>
            </date-picker>
        </div>
    </td>
</template>

<script>
//import Utils from '@/common/utils.js';
import EditableCellBase from './EditableCellBase.vue';
import ShowMessage from '@/components/messages/ShowMessage.js';

export default {
    name: 'EditableTimeCell',
    inject: ['$parentInstance'],
    extends: EditableCellBase,
    mixins: [ShowMessage],
    props: {
        value: {
            type: [Date, String],
            default: null
        },
        // '24h' or '12h'
        timeFormat: {
            type: String,
            default: '12h',
            validator: v => ['24h', '12h'].includes(v)
        },
        // 'mm' or 'mm:ss'
        precision: {
            type: String,
            default: 'mm',
            validator: v => ['mm', 'mm:ss'].includes(v)
        },
        invalidFormatAction: {
            type: String,
            default: 'initial',
            validator: value => ['null', 'initial'].includes(value)
        },
        invalidFormatWarning: {
            type: String,
            default: 'cell',
            validator: value => ['none', 'cell', 'message', 'both'].includes(value)
        }
    },

    data() {
        return {
            dropdownElement: null,
            showDropdown: false,
            displayValue: null,
            formattedValue: null,
            timeValue: null,
            dropdownRect: null
        };
    },
    computed: {
        dateFormat() {
            let result = this.timeFormat === '12h' ? 'hh:mm' : 'HH:mm';

            if (this.precision === 'mm:ss') {
                result += ':ss';
            }

            if (this.timeFormat === '12h') {
                result += ' A';
            }

            return result;
        },
        placeholder() {
            if (this.placeholderVisible) {
                /*let result = this.timeFormat === '12h' ? 'hh:mm' : 'HH:mm';

                if (this.precision === 'mm:ss') {
                    result += ':ss';
                }

                if (this.timeFormat === '12h') {
                    result += ' A';
                }

                return result;*/
                return this.dateFormat;
            }

            return '';
        },

        placeholderVisible() {
            return this.editableCell && this.timeValue === null;
        }
    },

    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                let cleanVal = newValue;
                const parsed = cleanVal ? this.parse(cleanVal) : null;
                this.displayValue = this.formattedValue = this.formatDate(parsed);
                this.timeValue = parsed;
            }
        },
        showDropdown(val) {
            val ? this.renderDropdown() : this.removeDropdown();
        },
        precision() {
            let cleanVal = this.value;
            const parsed = cleanVal ? this.parse(cleanVal) : null;
            this.displayValue = this.formattedValue = this.formatDate(parsed);
        },
        timeFormat() {
            let cleanVal = this.value;
            const parsed = cleanVal ? this.parse(cleanVal) : null;
            this.displayValue = this.formattedValue = this.formatDate(parsed);
        }
    },

    methods: {
        startEdit(char) {
            if (!this.editableCell) {
                this.displayContentFocus();
                return;
            }

            this.isEditing = true;
            if (char !== undefined) {
                this.$nextTick(() => {
                    const el = this.$refs.inputRef;
                    if (el) {
                        el.innerText = char;
                        el.focus();
                        this.moveCaretToEnd(el);
                    }
                });
            } else {
                this.inputContentFocus();
            }
        },
        finishEdit(event) {
            if (!this.showDropdown && this.isEditing) {
                this.isEditing = false;
                const val = event.target.innerText.trim();
                const changed = val !== String(this.formattedValue).trim();

                if (changed) {
                    const parsed = this.parse(val);
                    const valISO = this.emitInternalDate(parsed);
                    const valid = this.validateCell(valISO);

                    if (!valid) {
                        this.showMessage(
                            this.$tc('Msg.Error'),
                            this.invalidMessage(this.name),
                            'errorAutoHide'
                        );
                    }
                }
            }
        },
        closeDropdown() {
            this.showDropdown = false;
            this.finishEdit({ target: this.$refs.inputRef });
        },
        removeDropdown() {
            /*if (this.dropdownElement?.parentNode) {
                this.dropdownElement.parentNode.removeChild(this.dropdownElement);
                this.dropdownElement = null;
            }*/
        },
        toggleDropdown() {
            if (!this.editableCell) {
                return;
            }

            this.showDropdown = !this.showDropdown;

            if (this.showDropdown) {
                const val = this.$refs.inputRef.innerText.trim();
                this.timeValue = this.parse(val);
                this.$refs.inputRef.focus();
            }
        },
        renderDropdown() {
            this.$nextTick(() => {
                if (!this.$refs.cellRef) {
                    return;
                }

                const datePicker = document.getElementById(this.datePickerName);
                if (!datePicker) {
                    return;
                }

                var _this$_popupRect = this.dropdownRect,
                    width = _this$_popupRect.width,
                    height = _this$_popupRect.height;

                const _getRelativePosition = this.getRelativePosition(
                        this.$refs.cellRef,
                        width,
                        height,
                        true
                    ),
                    left = _getRelativePosition.left,
                    top = _getRelativePosition.top;

                Object.assign(datePicker.style, {
                    position: 'absolute',
                    top: top,
                    left: left,
                    zIndex: '999999'
                    //display: 'block'
                });

                /*const datePickerRect = datePicker.getBoundingClientRect();
                const rect = this.$refs.cellRef.getBoundingClientRect();
                const listHeight = datePickerRect.height;
                const spaceBelow = window.innerHeight - rect.bottom;
                const positionTop = spaceBelow < listHeight && rect.top > listHeight;
                const top1 = positionTop
                    ? rect.top + window.scrollY - listHeight
                    : rect.bottom + window.scrollY;

                console.log(top1, rect.left + window.scrollX);

                Object.assign(datePicker.style, {
                    position: 'absolute',
                    top: `${top1}px`,
                    left: `${rect.left + window.scrollX}px`,
                    zIndex: '999999'
                    //display: 'block'
                });*/
            });
        },
        requestEdit() {
            EditableCellBase.methods.requestEdit.call(this);

            this.$nextTick(() => {
                if (this.isEditing && !this.showDropdown) {
                    this.toggleDropdown();
                }
            });
        },
        parse(val) {
            if (!val) {
                return null;
            }

            if (val instanceof Date) {
                return val;
            }

            if (typeof val === 'string' && val.includes('T')) {
                const [, timePart] = val.split('T');

                let hour = 0;
                let minute = 0;
                let second = 0;

                if (timePart) {
                    const [hh, mm, ss = '00'] = timePart.split(':');
                    hour = parseInt(hh, 10) || 0;
                    minute = parseInt(mm, 10) || 0;
                    second = parseInt(ss, 10) || 0;
                }

                return new Date(1899, 11, 30, hour, minute, second);
            }

            let [datePart, timePart, period] = val.split(' ');
            if (datePart.includes(':')) {
                period = timePart;
                timePart = datePart;
            }

            let hour = 0;
            let minute = 0;
            let second = 0;

            if (timePart) {
                const [hh, mm, ss = '00'] = timePart.split(':');
                hour = parseInt(hh, 10) || 0;
                minute = parseInt(mm, 10) || 0;
                second = parseInt(ss, 10) || 0;
            }

            if (!this.isValidTime(hour, minute, second, period)) {
                return this.timeValue;
            }

            if (period) {
                const upperPeriod = period.toUpperCase();

                if (upperPeriod === 'PM' && hour < 12) {
                    hour += 12;
                } else if (upperPeriod === 'AM' && hour === 12) {
                    hour = 0;
                }
            }

            return new Date(1899, 11, 30, hour, minute, second);
        },
        formatDate(date) {
            if (!date) {
                return null;
            }

            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';

            if (this.timeFormat === '12h') {
                hours = hours % 12;

                if (hours === 0) {
                    hours = 12;
                }
            }

            const hh = String(hours).padStart(2, '0');
            if (this.precision === 'mm') {
                if (this.timeFormat === '12h') {
                    return `${hh}:${minutes} ${ampm}`;
                }

                return `${hh}:${minutes}`;
            }

            if (this.timeFormat === '12h') {
                return `${hh}:${minutes}:${seconds} ${ampm}`;
            }

            return `${hh}:${minutes}:${seconds}`;
        },
        isValidTime(hh, min, ss, period) {
            if (min < 0 || min > 59) {
                return false;
            }

            if (ss < 0 || ss > 59) {
                return false;
            }

            if (period) {
                if (period.toUpperCase() === 'PM') {
                    return hh > 0 && hh <= 12;
                } else if (period.toUpperCase() === 'AM') {
                    return hh >= 0 && hh <= 11;
                } else {
                    return false;
                }
            } else if (hh < 0 || hh > 23) {
                return false;
            }

            return true;
        },
        emitInternalDate(val) {
            if (!val) {
                if (this.isAppendSection) {
                    this.$emit('input', null);
                } else {
                    this.$parentInstance.cellEditComplete(this.createEventData(null));
                }

                return null;
            }

            const pad = n => String(n).padStart(2, '0');
            const yyyy = val.getFullYear();
            const mm = pad(val.getMonth() + 1);
            const dd = pad(val.getDate());
            const hh = pad(val.getHours());
            const mi = pad(val.getMinutes());
            const ss = this.precision === 'mm:ss' ? pad(val.getSeconds()) : '00';

            let result = `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;

            if (this.isAppendSection) {
                this.$emit('input', result);
            } else {
                this.$parentInstance.cellEditComplete(this.createEventData(result));
            }

            return result;
        },
        handleKeydown(event) {
            const allowedChars = ['a', 'A', 'm', 'M', 'p', 'P', ':', ' '];

            if (/^[0-9]$/.test(event.key) || allowedChars.includes(event.key)) {
                return;
            }

            const allowedKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Delete',
                'Escape',
                'Enter'
            ];

            if (allowedKeys.includes(event.key)) {
                switch (event.key) {
                    case 'Enter':
                    case 'Tab':
                    case 'Escape':
                        this.showDropdown = false;
                        break;
                    case 'ArrowUp':
                        if (this.caretAtStart()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowDown':
                        if (this.caretAtEnd()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowLeft':
                        if (this.caretAtStart() && !this.isAllSelected()) {
                            this.showDropdown = false;
                        }
                        break;
                    case 'ArrowRight':
                        if (this.caretAtEnd() && !this.isAllSelected()) {
                            this.showDropdown = false;
                        }
                        break;
                }

                EditableCellBase.methods.handleKeydown.call(this, event);
                return;
            }

            event.preventDefault();
        },
        datePickerChanged() {
            const formattedValue = this.formatDate(this.timeValue);
            //this.showDropdown = false;

            const el = this.$refs.inputRef;
            if (el) {
                el.innerText = formattedValue;
                el.focus();
                this.moveCaretToEnd(el);
            }
        },
        selectToday() {
            this.timeValue = new Date();
            this.datePickerChanged();
            this.showDropdown = false;
        }
        /*enterEditMode() {
            this.isEditing = true;
            this.$nextTick(() => {
                this.$refs.textInput.focus();
            });
        },

        handleEnter() {
            if (this.isEditing) {
                this.isEditing = false;
            }
            this.$emit('next-cell');
        },

        applyTimeMask() {
            let result = '';

            if (this.rawDigits.length > 0) {
                result += this.rawDigits.substring(0, 2);

                if (this.rawDigits.length > 2) {
                    result += ':' + this.rawDigits.substring(2, 4);

                    if (
                        this.precision === 'mm:ss' &&
                        this.rawDigits.length > 4
                    ) {
                        result += ':' + this.rawDigits.substring(4, 6);
                    }
                }
            }

            if (this.timeFormat === '12h' && result.length >= 5) {
                result += ' ' + (this.period || 'AM');
            }

            return result;
        },

        onBlur() {
            this.isEditing = false;
            this.emitIfValid();
        },

        onKeyDown(e) {
            const input = e.target;
            const cursorPos = input.selectionStart;

            const allowedKeys = [
                'Backspace',
                'Tab',
                'ArrowLeft',
                'ArrowRight',
                'Delete'
            ];

            if (allowedKeys.includes(e.key)) {
                return;
            }

            // Toggle AM/PM with A or P
            if (this.timeFormat === '12h') {
                if (e.key.toUpperCase() === 'A') {
                    this.period = 'AM';
                    e.preventDefault();
                    return;
                }

                if (e.key.toUpperCase() === 'P') {
                    this.period = 'PM';
                    e.preventDefault();
                    return;
                }
            }

            // Allow colon only at valid positions
            if (e.key === ':') {
                if (cursorPos !== 2 && cursorPos !== 5) {
                    e.preventDefault();
                }
                return;
            }

            // Allow digits only
            if (!/^\d$/.test(e.key)) {
                e.preventDefault();
            }
        },

        onInput(e) {
            const input = e.target;
            const cursorPos = input.selectionStart;

            let digits = input.value.replace(/\D/g, '');

            // HHmm or HHmmss
            digits = digits.substring(
                0,
                this.precision === 'mm:ss' ? 6 : 4
            );

            this.rawDigits = digits;
            this.Value = this.applyTimeMask();

            input.value = this.Value;

            this.$nextTick(() => {
                const newCursorPos = Math.min(
                    cursorPos,
                    this.Value.length
                );

                input.setSelectionRange(newCursorPos, newCursorPos);
            });

            this.emitIfValid();
        },

        emitIfValid() {
            const expectedLength =
                this.precision === 'mm:ss' ? 6 : 4;

            if (this.rawDigits.length !== expectedLength) {
                this.emitChanged('');
                return;
            }

            let hours = parseInt(this.rawDigits.substring(0, 2), 10);
            const minutes = this.rawDigits.substring(2, 4);
            const seconds =
                this.precision === 'mm:ss'
                ? this.rawDigits.substring(4, 6)
                : '00';

            if (this.timeFormat === '12h') {
                if (this.period === 'PM' && hours < 12) {
                    hours += 12;
                }

                if (this.period === 'AM' && hours === 12) {
                    hours = 0;
                }
            }

            if (hours > 23 || minutes > 59 || seconds > 59) {
                this.emitChanged('');
                return;
            }

            const isoTime = `${hours
                .toString()
                .padStart(2, '0')}:${minutes}:${seconds}`;

            this.$emit('input', isoTime);
            this.emitChanged(isoTime);
        },

        onPaste(e) {
            e.preventDefault();

            const pasted = (
                e.clipboardData || window.clipboardData
            ).getData('text');

            const digits = pasted.replace(/\D/g, '');

            if (!digits) {
                return;
            }

            this.rawDigits = digits.substring(
                0,
                this.precision === 'mm:ss' ? 6 : 4
            );

            if (this.timeFormat === '12h') {
                if (/pm/i.test(pasted)) {
                    this.period = 'PM';
                }

                if (/am/i.test(pasted)) {
                    this.period = 'AM';
                }
            }

            this.Value = this.applyTimeMask();
        },

        invalidateState(state) {
            this.$nextTick(() => this.invalid = state);
        },

        emitChanged(value) {
            const changed = {
                "Changed":true,
                "ComponentName":this.name,
                "Value" : value
            }
            EventBus.$emit('onChanged', changed);
        }*/
    },

    mounted() {
        const datePicker = document.getElementById(this.datePickerName);

        if (datePicker) {
            this.dropdownRect = this.getElementSize(datePicker);
            document.body.appendChild(datePicker);
        }
    },

    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = e => {
                    const datePicker = document.getElementById(binding.arg);
                    const insideDatePicker = datePicker && datePicker.contains(e.target);

                    if (!(el === e.target || el.contains(e.target) || insideDatePicker)) {
                        vnode.context[binding.expression]();
                    }
                };
                document.body.addEventListener('mousedown', el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener('mousedown', el.clickOutsideEvent);
            }
        }
    }
};
</script>

<style scoped>
.md-editable-grid-cell-display {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.editable-date-cell-content-wrapper {
    display: flex;
    align-items: stretch;
    width: 100%;
    height: 100%;
    position: relative;
}

.editable-date-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}
tbody.md-editable-grid-body-append .editable-date-cell-icon-container {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #19bee8;
    cursor: pointer;
    z-index: 11;
}

/* Display mode: flex-based so the icon stays within the cell scroll area */
.editable-date-cell-display-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
.editable-date-cell-display-wrapper .md-editable-grid-cell-display {
    flex: 1;
    min-width: 0;
}
.editable-date-cell-display-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-right: 6px;
    color: #19bee8;
    cursor: pointer;
}
tbody.md-editable-grid-body-append .editable-date-cell-display-icon {
    color: #19bee8;
}
.span-placeholder {
    color: lightgrey;
}
</style>
